import { useSyncExternalStore, type ReactNode } from "react";
import type { DataTableSource, TableRow } from "@carbon6/c6-frontend-design";
import { RefreshIcon } from "@/components/icons";
import { isTextNode, TableEmptyState, TableLoadingState } from "./TableEmptyState";

type TableStateMessages = {
  emptyMessage: ReactNode;
  loadingMessage: ReactNode;
  errorMessage: ReactNode;
};

/**
 * 표·격자의 빈·대기·실패 자리를 한 형태로 만든다.
 *
 * 호출부는 문장만 넘긴다. 이 hook 이 `EmptyState`·`LoadingState` 로 감싼다. 세 자리는 같은 크기와 면을
 * 쓴다. 그래서 대기에서 빈 상태·실패로 바뀔 때 높이가 바뀌지 않는다. 요소 노드를 넘기면 그대로 둔다.
 * 그 노드가 형태를 소유한다(예: 동작 버튼이 있는 빈 상태).
 *
 * 실패 자리는 source 의 `errorMessage` 와 `다시 시도` 를 그린다. c6 는 페이지 모드에서 첫 조회가 실패하고
 * 행이 없을 때만 이 자리를 쓴다. 행이 남아 있는 실패는 표 밖의 오류 띠가 알린다(`tableStripError`).
 *
 * @param source 표가 구독하는 source. 실패 문장과 다시 시도가 이 source 를 쓴다.
 * @param messages 호출부가 넘긴 빈 상태·대기 문장.
 *   예) `{ emptyMessage: "등록된 카메라가 없습니다.", loadingMessage: undefined }`
 * @returns c6 `DataTable`·`DataGridView` 의 `emptyMessage`·`loadingMessage`·`errorMessage` 에 넘길 노드.
 */
export function useTableStateMessages<Row extends TableRow>(
  source: DataTableSource<Row>,
  {
    emptyMessage = "표시할 항목이 없습니다.",
    loadingMessage = "불러오고 있습니다.",
  }: { emptyMessage?: ReactNode; loadingMessage?: ReactNode },
): TableStateMessages {
  // source 의 실패 문장만 구독한다. 행이 바뀔 때는 다시 그리지 않는다.
  const sourceError = useSyncExternalStore(
    source.subscribe,
    () => source.getSnapshot().errorMessage,
  );
  return {
    emptyMessage: isTextNode(emptyMessage) ? (
      <TableEmptyState>{emptyMessage}</TableEmptyState>
    ) : (
      emptyMessage
    ),
    loadingMessage: isTextNode(loadingMessage) ? (
      <TableLoadingState>{loadingMessage}</TableLoadingState>
    ) : (
      loadingMessage
    ),
    errorMessage: (
      <TableEmptyState
        tone="error"
        action={{
          label: "다시 시도",
          onClick: () => void source.refresh(),
          icon: <RefreshIcon />,
        }}
      >
        {sourceError ?? "목록을 불러오지 못했습니다."}
      </TableEmptyState>
    ),
  };
}

/**
 * 표 밖의 오류 띠에 넘길 조회 실패 문장을 고른다.
 *
 * 행이 없는 실패는 표 본문이 알린다(페이지 모드는 실패 자리, append 모드는 c6 footer). 그때 띠는
 * 숨긴다. 같은 사유를 두 번 알리지 않는다. 행이 남아 있는 실패는 본문에 자리가 없어서 띠가 알린다.
 *
 * @param snapshot 표 source 의 snapshot.
 * @returns 띠에 표시할 문장. 본문이 알리거나 실패가 없으면 null.
 */
export function tableStripError(snapshot: {
  rows: readonly unknown[];
  errorMessage: string | null;
}): string | null {
  return snapshot.rows.length === 0 ? null : snapshot.errorMessage;
}
